/**
 * AuthorisedDeliveryChain - Compact Lifecycle Ribbon
 * 
 * Shows how work moves from idea to production under governance.
 * A cross-cutting visual that unifies the existing demos into one enterprise story.
 * 
 * "Most organisations can track tasks. Far fewer can prove how an idea became
 * an authorised requirement, how work was delegated, what changed, and why
 * a production release was allowed."
 */

import React, { useState } from 'react';
import { 
  Lightbulb,
  FileCheck,
  Users,
  GitBranch,
  Rocket,
  ChevronRight,
  Shield,
  X,
  AlertOctagon
} from 'lucide-react';
import { Badge } from '../ui/badge';

// Stage data with example content
const STAGES = [
  {
    id: 'idea',
    title: 'Idea Logged',
    icon: Lightbulb,
    color: 'cyan',
    status: 'complete',
    actor: 'Research Lead',
    actorRole: 'Business Sponsor',
    action: 'Submitted opportunity statement for accessible travel advisor',
    authority: 'Innovation Intake Policy',
    receipt: 'IDEA-2026-0412',
    outcome: 'Accepted for review',
    outcomeStyle: 'success'
  },
  {
    id: 'requirement',
    title: 'Requirement Authorised',
    icon: FileCheck,
    color: 'emerald',
    status: 'complete',
    actor: 'CEO / Sponsor Node',
    actorRole: 'Funding Authority',
    action: 'Approved requirement with budget envelope and success criteria',
    authority: 'Portfolio Investment Authority',
    receipt: 'REQ-AUTH-0412',
    outcome: 'Approved',
    outcomeStyle: 'success'
  },
  {
    id: 'delegation',
    title: 'Work Delegated',
    icon: Users,
    color: 'purple',
    status: 'complete',
    actor: 'CTO Node',
    actorRole: 'Technical Delivery Authority',
    action: 'Allocated bounded development tasks to platform and accessibility teams',
    authority: 'Technical Delivery Authority',
    receipt: 'DEL-TASK-0412',
    outcome: 'Work allocated',
    outcomeStyle: 'success'
  },
  {
    id: 'change',
    title: 'Change Governed',
    icon: GitBranch,
    color: 'amber',
    status: 'quarantined',
    actor: 'Developer + Reviewer',
    actorRole: 'Engineering Authority',
    action: 'Proposed schema change to advisory-input-schema.json',
    authority: 'Change Review Policy',
    receipt: 'CHG-REV-0412',
    outcome: 'Quarantined',
    outcomeNote: 'Change would bypass source verification',
    outcomeStyle: 'warning'
  },
  {
    id: 'release',
    title: 'Release Allowed',
    icon: Rocket,
    color: 'rose',
    status: 'pending',
    actor: 'Release Authority',
    actorRole: 'Production Gate',
    action: 'Awaiting safe change before deployment approval',
    authority: 'Production Release Policy',
    receipt: 'Pending',
    outcome: 'Blocked',
    outcomeNote: 'Upstream change quarantined',
    outcomeStyle: 'blocked'
  }
];

// Color utilities
const getColorClasses = (color, type) => {
  const colors = {
    cyan: {
      bg: 'bg-cyan-500/20',
      border: 'border-cyan-500/50',
      text: 'text-cyan-400',
      ring: 'ring-cyan-500/30'
    },
    emerald: {
      bg: 'bg-emerald-500/20',
      border: 'border-emerald-500/50',
      text: 'text-emerald-400',
      ring: 'ring-emerald-500/30'
    },
    purple: {
      bg: 'bg-purple-500/20',
      border: 'border-purple-500/50',
      text: 'text-purple-400',
      ring: 'ring-purple-500/30'
    },
    amber: {
      bg: 'bg-amber-500/20',
      border: 'border-amber-500/50',
      text: 'text-amber-400',
      ring: 'ring-amber-500/30'
    },
    rose: {
      bg: 'bg-rose-500/20',
      border: 'border-rose-500/50',
      text: 'text-rose-400',
      ring: 'ring-rose-500/30'
    }
  };
  return colors[color]?.[type] || '';
};

const getOutcomeStyle = (style) => {
  const styles = {
    success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    warning: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    blocked: 'bg-slate-500/10 border-slate-500/30 text-slate-400'
  };
  return styles[style] || styles.success;
};

// Stage node component
const StageNode = ({ stage, isActive, onClick, isLast }) => {
  const Icon = stage.icon;
  const isQuarantined = stage.status === 'quarantined';
  const isPending = stage.status === 'pending';
  
  return (
    <div className="flex items-center">
      <button
        onClick={() => onClick(stage.id)}
        className={`
          relative flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-200
          ${isActive ? `${getColorClasses(stage.color, 'bg')} ring-2 ${getColorClasses(stage.color, 'ring')}` : 'hover:bg-slate-800/50'}
          ${isPending ? 'opacity-60' : ''}
        `}
      >
        {/* Status indicator for quarantined */}
        {isQuarantined && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
            <AlertOctagon className="w-3 h-3 text-slate-900" />
          </div>
        )}
        
        {/* Icon */}
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center
          ${getColorClasses(stage.color, 'bg')} ${getColorClasses(stage.color, 'border')} border
        `}>
          <Icon className={`w-5 h-5 ${getColorClasses(stage.color, 'text')}`} />
        </div>
        
        {/* Title */}
        <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-slate-400'} whitespace-nowrap`}>
          {stage.title}
        </span>
      </button>
      
      {/* Connector */}
      {!isLast && (
        <div className="flex items-center px-1">
          <div className={`w-8 h-0.5 ${isPending ? 'bg-slate-700' : 'bg-slate-600'}`} />
          <ChevronRight className={`w-4 h-4 ${isPending ? 'text-slate-700' : 'text-slate-600'}`} />
        </div>
      )}
    </div>
  );
};

// Detail panel component
const StageDetail = ({ stage, onClose }) => {
  if (!stage) return null;
  
  const Icon = stage.icon;
  const isQuarantined = stage.status === 'quarantined';
  
  return (
    <div className={`
      mt-4 p-4 rounded-xl border transition-all duration-300
      ${getColorClasses(stage.color, 'bg')} ${getColorClasses(stage.color, 'border')}
    `}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`
            w-8 h-8 rounded-lg flex items-center justify-center
            ${getColorClasses(stage.color, 'bg')}
          `}>
            <Icon className={`w-4 h-4 ${getColorClasses(stage.color, 'text')}`} />
          </div>
          <div>
            <h4 className="text-white font-medium">{stage.title}</h4>
            <p className="text-xs text-slate-400">{stage.actor} · {stage.actorRole}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-xs text-slate-500 mb-1">Action</div>
          <p className="text-slate-300">{stage.action}</p>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Authority</div>
          <p className="text-slate-300">{stage.authority}</p>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Receipt</div>
          <code className={`text-xs ${getColorClasses(stage.color, 'text')}`}>{stage.receipt}</code>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Outcome</div>
          <Badge className={`${getOutcomeStyle(stage.outcomeStyle)} border text-xs`}>
            {stage.outcome}
          </Badge>
          {stage.outcomeNote && (
            <p className="text-xs text-slate-400 mt-1">{stage.outcomeNote}</p>
          )}
        </div>
      </div>
      
      {isQuarantined && (
        <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-2">
          <Shield className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-300">
            Governance intervention: This change was quarantined because it would bypass source verification controls. 
            The delivery chain cannot progress until a safe change is approved.
          </p>
        </div>
      )}
    </div>
  );
};

// Main component
const AuthorisedDeliveryChain = () => {
  const [activeStage, setActiveStage] = useState(null);
  
  const handleStageClick = (stageId) => {
    setActiveStage(activeStage === stageId ? null : stageId);
  };
  
  const activeStageData = STAGES.find(s => s.id === activeStage);
  
  return (
    <div className="mb-10">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-white mb-1">
          From idea to production with attributable governance
        </h2>
        <p className="text-sm text-slate-400">
          Who acted, under what authority, with what evidence, and why the next step was allowed
        </p>
      </div>
      
      {/* Ribbon */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-4">
        {/* Stage nodes */}
        <div className="flex items-center justify-center flex-wrap gap-y-4">
          {STAGES.map((stage, index) => (
            <StageNode
              key={stage.id}
              stage={stage}
              isActive={activeStage === stage.id}
              onClick={handleStageClick}
              isLast={index === STAGES.length - 1}
            />
          ))}
        </div>
        
        {/* Detail panel */}
        {activeStageData && (
          <StageDetail 
            stage={activeStageData} 
            onClose={() => setActiveStage(null)} 
          />
        )}
        
        {/* Subtle hint when no stage selected */}
        {!activeStage && (
          <p className="text-center text-xs text-slate-500 mt-3">
            Click any stage to see who acted, under what authority, and with what evidence
          </p>
        )}
      </div>
      
      {/* Business message */}
      <p className="text-center text-xs text-slate-500 mt-3 max-w-2xl mx-auto">
        Most organisations can track tasks. Far fewer can prove how an idea became an authorised requirement, 
        how work was delegated, what changed, and why a production release was allowed.
      </p>
    </div>
  );
};

export default AuthorisedDeliveryChain;
